// hooks, auth, navigation
import React, {useState, useEffect, useMemo} from 'react';

// Material UI
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import Dialog from '@mui/material/Dialog';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { CircularProgress } from "@mui/material";

// Material UI icons
import CloseIcon from '@mui/icons-material/Close';

// My Modules
import MDButton from "components/MDButton";
import { useCurrencyCodes } from 'hooks/useCurrencyCodes';
import {outlinedFieldProps} from "../trips/stepForm/utils/fieldStyles";
import Autocomplete from "@mui/material/Autocomplete";

const HighSeasonSurchargeModal = ({ open, onClose, onSave, template, isSaving = false }) => {

    // Initializing states

    const { toDropdownOptions } = useCurrencyCodes();
    const availableCurrencies = toDropdownOptions({ labelFormat: 'codeName' });
    // const { availableCurrencies } = useCurrency();
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        template_name: '',
        hss_percentage: '',
        valid_from: '',
        valid_until: '',
        currency: '',
        status: 'draft',
    });

    // Validate input values.

    const validate = () => {
        const newErrors = {};

        // These field must be filled.
        if (!formData.template_name) newErrors.template_name = '*This field is required';
        if (formData.hss_percentage === '' || formData.hss_percentage == null) {
            newErrors.hss_percentage = '*This field is required';
        }
        if (!formData.valid_from) newErrors.valid_from = '*This field is required';
        if (!formData.valid_until) newErrors.valid_until = '*This field is required';
        if (!formData.currency) newErrors.currency = '*This field is required';
        if (!formData.status) newErrors.status = '*This field is required';

        // HSS percentage must be between 0 and 100
        if (
            formData.hss_percentage != null &&
            (formData.hss_percentage < 0 || formData.hss_percentage > 100)
        ) {
            newErrors.hss_percentage = 'Must be between 0 and 100';
        }

        // End date must be after start date
        if (formData.valid_from && formData.valid_until) {
            const startDate = new Date(formData.valid_from);
            const endDate = new Date(formData.valid_until);

            if (endDate < startDate) {
                newErrors.valid_until = 'End date must be after start date';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }


    useEffect(() => {
        if (open) {
            // Prefill the data if template prop is passed - when edit is performed.
            if (template) {
                setFormData({
                    template_name: template.template_name || '',
                    hss_percentage: template.hss_percentage ?? '',  // ?? not || — 0% would be wiped by ||
                    valid_from: template.valid_from || '',
                    valid_until: template.valid_until || '',
                    currency: template.currency || '',
                    status: template.status || 'draft',
                });
            // Reset form when modal closes.
            } else {
                setFormData({
                    template_name: '',
                    hss_percentage: '',
                    valid_from: '',
                    valid_until: '',
                    currency: '',
                    status: 'draft',
                });
            }
            setErrors({});
        }
    }, [open, template]);

    // Update form data whenever user types in something.

    const handleChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    }

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose();
            }}
            maxWidth="sm"
            fullWidth
        >

            {/* Dialog Header */}

            {/* CHECK THIS : very minimum visual bug :
             on editing > clicking close > there is a flick of text
             change from Edit to create.
             */}

            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {template ? 'Edit HSS Template' : 'Create HSS Template'}
                <IconButton onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            {/* Dialog Form */}

            <DialogContent>

                <Grid container spacing={2} sx={{ mb: 2 }}>

                    {/* Form Field - Template Name */}

                    <Grid item xs={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ position: 'relative' }}>
                                <Typography variant="caption">Template Name *</Typography>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="e.g. Standard Split"
                                    value={formData.template_name}
                                    onChange={(e) => handleChange('template_name', e.target.value)}
                                    error={errors.template_name}
                                />
                                {errors.template_name && (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ position: 'absolute', top: '100%', left: 0, mt: 0.25 }}
                                    >
                                        {errors.template_name}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Grid>

                    {/* Form Field - HSS Percentage % */}

                    <Grid item xs={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ position: 'relative' }}>
                                <Typography variant="caption">HSS Percentage (%) *</Typography>
                                <TextField
                                    size="small"
                                    type="number"
                                    fullWidth
                                    placeholder=""
                                    value={formData.hss_percentage}
                                    onChange={(e) => handleChange('hss_percentage', e.target.value)}
                                    error={errors.hss_percentage}
                                />
                                {errors.hss_percentage && (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ position: 'absolute', top: '100%', left: 0, mt: 0.25 }}
                                    >
                                        {errors.hss_percentage}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Grid>

                    {/* Form Field : Valid From */}

                    <Grid item xs={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ position: 'relative' }}>
                                <Typography variant="caption">Valid From (Season Start) *</Typography>
                                <TextField
                                    size="small"
                                    type="date"
                                    fullWidth
                                    placeholder=""
                                    value={formData.valid_from}
                                    onChange={(e) => handleChange('valid_from', e.target.value)}
                                    error={errors.valid_from}
                                />
                                {errors.valid_from && (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ position: 'absolute', top: '100%', left: 0, mt: 0.25 }}
                                    >
                                        {errors.valid_from}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Grid>

                    {/* Form Field : Valid until */}

                    <Grid item xs={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ position: 'relative' }}>
                                <Typography variant="caption">Valid Until (Season End) *</Typography>
                                <TextField
                                    size="small"
                                    type="date"
                                    fullWidth
                                    placeholder=""
                                    value={formData.valid_until}
                                    onChange={(e) => handleChange('valid_until', e.target.value)}
                                    error={errors.valid_until}
                                />
                                {errors.valid_until && (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ position: 'absolute', top: '100%', left: 0, mt: 0.25 }}
                                    >
                                        {errors.valid_until}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Grid>

                    {/* Form Field : Currency dropdown*/}

                    <Grid item xs={6}>
                        <Box sx={{ position: 'relative' }}>
                            <Typography variant="caption">Currency *</Typography>
                            <Autocomplete
                                size="small"
                                fullWidth
                                value={formData.currency?.toUpperCase() ?? ''}
                                // onChange={(e) => handleChange('currency', e.target.value.toUpperCase())}
                                onChange={(event, newValue) => {
                                    handleChange('currency', newValue ? newValue.value : '');
                                }}
                                options={availableCurrencies}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.value}>
                                        {option.label}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        {...outlinedFieldProps}
                                        error={errors.currency}
                                        placeholder="Select a currency"
                                        helperText={errors.currency ? (errors.currency || 'This field is required') : ''}
                                    />
                                )}
                            />
                        </Box>
                    </Grid>

                    {/* Form Field : Status Dropdown */}

                    <Grid item xs={6}>
                        <Box sx={{ position: 'relative' }}>
                            <Typography variant="caption">Status *</Typography>
                            <Select
                                size="small"
                                fullWidth
                                value={formData.status}
                                error={errors.status}
                                onChange={(e) => handleChange('status', e.target.value)}
                            >
                                <MenuItem value="draft">Draft</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                            {errors.status && (
                                <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ position: 'absolute', top: '100%', left: 0, mt: 0.25 }}
                                >
                                    {errors.status}
                                </Typography>
                            )}
                        </Box>
                    </Grid>

                </Grid>

            </DialogContent>

            {/* Dialog Action Buttons*/}

            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
                <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={onClose}
                    sx={{ marginRight: 1 }}
                >
                    Cancel
                </MDButton>
                <MDButton
                    variant="gradient"
                    color="info"
                    disabled={isSaving ? true : false}
                    onClick={() => {
                        if (validate()) {
                            onSave(formData); // let the parent close the modal
                        }
                    }}
                    sx={{
                        px: 3,
                        py: 1.5,
                        fontSize: '14px',
                        fontWeight: '600',
                        textTransform: 'none',
                        borderRadius: 2,
                        color: '#fff',
                        '& .MuiButton-label, & span': { color: '#fff' },
                    }}
                >
                    {
                        isSaving && <CircularProgress size={16} thickness={5} sx={{ color: "#1565C8", mr: 1 }} />
                    }
                    { isSaving ? "Saving..." : "Save Template"}
                </MDButton>
            </DialogActions>
        </Dialog>
    )
}

export default HighSeasonSurchargeModal;